<script setup lang="ts">
import {
  DocumentTextIcon,
  GlobeAltIcon,
  MusicalNoteIcon,
  PaperClipIcon,
  PhotoIcon,
  VideoCameraIcon,
} from '@heroicons/vue/24/outline'
import type { Component, HTMLAttributes, VNode } from 'vue'
import { computed } from 'vue'
import { cn } from '../utils'
import { useAttachmentContext } from './context'
import type { AttachmentMediaCategory } from './types'

interface Props {
  class?: HTMLAttributes['class']
  fallbackIcon?: VNode
}

const props = defineProps<Props>()

const { data, mediaCategory, variant } = useAttachmentContext()

const isGrid = computed(() => variant.value === 'grid')
const iconSize = computed(() =>
  variant.value === 'inline' ? 'size-3' : 'size-4'
)
const fileUrl = computed(() =>
  data.value.type === 'file' ? data.value.url : undefined
)
const showImage = computed(
  () =>
    mediaCategory.value === 'image' &&
    data.value.type === 'file' &&
    !!fileUrl.value
)
const showVideo = computed(
  () =>
    mediaCategory.value === 'video' &&
    data.value.type === 'file' &&
    !!fileUrl.value
)

const iconMap: Record<AttachmentMediaCategory, Component> = {
  image: PhotoIcon,
  video: VideoCameraIcon,
  audio: MusicalNoteIcon,
  source: GlobeAltIcon,
  document: DocumentTextIcon,
  unknown: PaperClipIcon,
}

const iconComponent = computed(() => iconMap[mediaCategory.value])
const imageAlt = computed(() =>
  data.value.type === 'file' ? data.value.filename || 'Image' : 'Image'
)
</script>

<template>
  <div
    :class="
      cn(
        'flex shrink-0 items-center justify-center overflow-hidden',
        variant === 'grid' && 'size-full bg-gray-100 dark:bg-white/5',
        variant === 'inline' && 'size-5 rounded bg-white dark:bg-gray-800',
        variant === 'list' && 'size-12 rounded bg-gray-100 dark:bg-white/5',
        props.class
      )
    "
    v-bind="$attrs"
  >
    <img
      v-if="showImage"
      :alt="imageAlt"
      :class="
        isGrid ? 'size-full object-cover' : 'size-full rounded object-cover'
      "
      :height="isGrid ? 96 : 20"
      :src="fileUrl"
      :width="isGrid ? 96 : 20"
    />
    <video
      v-else-if="showVideo"
      class="size-full object-cover"
      muted
      :src="fileUrl"
    />
    <component :is="props.fallbackIcon" v-else-if="props.fallbackIcon" />
    <component
      :is="iconComponent"
      v-else
      :class="cn(iconSize, 'text-gray-400')"
    />
  </div>
</template>
